import { SliderHeader } from "../../../ui/slider/SliderHeader.js";
import { Slider } from "../../../ui/slider/Slider.js";
import { FilterUi } from "./FilterUi.js";
import { roundNumber } from "../../../utils/utils.js";
import { FilterSettings } from "../FilterSettings.js";

export { RangeFilter };

/**
 * A class that defines the Ui for a Slider-based Range Filter
 * 
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.map.filters.ui
 */
class RangeFilter extends FilterUi {

    /**
     * @param {HTMLElement | string} target
     * @param {object} options filter specific options
     * @param {LayerCache} layers Used for included/excluded layers
     */
    constructor(target, options, layers) {

        // apply default options
        options = Object.assign({
            title: "Filter By Range",
            allowShowNullValues: true,
            showNullValues: true,
            column: "",
            decimals: 0,
            begin: 0,
            end: 100,
            operators: [
                "-",
                "=",
                ">",
                "≥",
                "<",
                "≤"
            ],
            initialOperator: "-",
            initialBegin: 0,
            initialEnd: 100,
        }, options || {});

        super(target, options, layers);

        this.slider = null;

        this.settingsClass_ = RangeFilterSettings;
        this.currentValue_ = null
        this.operator_ = options.initialOperator || options.operators[0];

        const container = this.getContentContainer();
        container.classList.add("range-filter");
        container.style.paddingBottom = "5px";

        this.initSlider_();
        this.setTitle(this.options_.title);
        this.initShowNullButton_();
    }

    /**
     * Init a slider header and its events including
     * validation of the input
     * 
     * @param {Slider} slider
     * @param {string} type
     * @param {string[]} operators
     * @returns {SliderHeader} header
     */
    initHeader_(slider) {
        const header = new SliderHeader(null, {
            title: "VALUES",
            operators: this.options_.operators,
            defaultOperator: this.operator_,
            useInput: true,
            inputPattern: "^([0-9]+(.?[0-9]+)?)$"
        });

        const validCharacters = "0123456789.,";
        const removeInvalidCharacters = (str) => {
            for (let i = str.length - 1; i >= 0; --i) {
                if (!validCharacters.includes(str[i])) str = str.replace(str[i], "");
            }
            return str;
        }

        // validation and auto-complete minus character or end
        header.on("input", (e) => {
            let value = removeInvalidCharacters(e.value);
            if (value.includes(",")) value = value.replaceAll(",", ".");
            if (value.startsWith(".")) value = "0" + value;

            // remove additional decimal seperators
            const parts = value.split(".");
            if (parts.length > 2) {
                value = "";
                let addedSeperator = false;
                for (let i = 0; i < parts.length; ++i) {
                    value += parts[i];
                    if (!addedSeperator) {
                        value += ".";
                        addedSeperator = true;
                    }
                }
            }

            header.setValue(e.type, value);
        });

        // operator selection
        header.on("select", operator => {
            this.operator_ = operator;
            this.initSlider_();
            this.fire("change", this);
        });

        // apply the header value to the slider
        header.on("change", value => this.parseHeaderValue_(value));

        // insert before slider
        slider.getElement().insertAdjacentElement("beforebegin", header.getElement())

        return header;
    }

    /**
     * internal method for initializing the slider.
     * the slider gets re-initialized everytime the
     * operator changes.
     * 
     * @private
     */
    initSlider_() {
        const options = this.options_;

        // adjust operator
        if (!options.operators.includes(this.operator_)) {
            if (options.operators.length == 0) options.operators = ["-"];
            this.operator_ = options.operators[0];
        }

        // remove slider
        if (this.slider_) this.slider_.dispose();
        this.slider_ = null;

        const sliderOptions = {
            min: options.begin,
            max: options.end
        }

        const previousLeft = (Number.isFinite(this.currentValue_) || (this.currentValue_ == null)) ? this.currentValue_ : this.currentValue_.left;
        const previousRight = (Number.isFinite(this.currentValue_) || (this.currentValue_ == null)) ? null : this.currentValue_.right;

        if ("-" == this.operator_) {
            sliderOptions.handles = 2;
            sliderOptions.value = {
                left: previousLeft || options.initialBegin || options.begin,
                right: previousRight || options.initialEnd || options.end
            }
        } else {
            sliderOptions.handles = 1;
            sliderOptions.value = previousLeft || options.initialBegin || options.begin;
        }

        const container = document.createElement("div");
        const slider = new Slider(container, sliderOptions);
        const header = this.initHeader_(slider)

        const updateValue = val => {
            let values = val;

            if (Number.isFinite(val)) {
                values = roundNumber(val, options.decimals)
            } else {
                values = {
                    left: roundNumber(val.left, options.decimals),
                    right: roundNumber(val.right, options.decimals),
                };
            }

            header.setValues({
                begin: (Number.isFinite(values)) ? values : values.left,
                end: (Number.isFinite(values)) ? values : values.right
            });

            this.currentValue_ = values;
        };

        const dispose = () => {
            header.dispose();
            slider.dispose();
            container.remove();
        };

        const item = {
            container: container,
            header: header,
            slider: slider,
            dispose: dispose
        };

        slider.on("change", (value) => {
            updateValue(value);
        });
        slider.on("stop", (value) => {
            updateValue(value);
            this.fire("change", this);
        });
        slider.on("arrow_right", handleName => this.increment(handleName));
        slider.on("arrow_left", handleName => this.decrement(handleName));
        header.on("arrow_up", inputName => this.increment((inputName == "begin") ? "left" : "right"));
        header.on("arrow_down", inputName => this.decrement((inputName == "begin") ? "left" : "right"));

        updateValue(slider.getValue());
        this.slider_ = item;
        this.getContentContainer().appendChild(container);
    }

    reloadOptions_() {
        this.setTitle(this.options_.title);
        this.toggleToolVisibility("btn-power", this.options_.deactivatable);
        this.toggleToolVisibility("btn-show-null", this.options_.allowShowNullValues);
        this.initSlider_();
        this.fire("change", this);
    }

    increment(handle) {
        const value = this.slider_.slider.getValue();
        if (Number.isFinite(value)) {
            this.slider_.slider.setValue(++value);
        } else {
            ++value[handle];
            this.slider_.slider.setValue(value);
        }
        this.fire("change", this);
    }

    decrement(handle) {
        const value = this.slider_.slider.getValue();
        if (Number.isFinite(value)) {
            this.slider_.slider.setValue(--value);
        } else {
            --value[handle];
            this.slider_.slider.setValue(value);
        }
        this.fire("change", this);
    }

    /**
     * @private
     * @param {object} value 
     */
    parseHeaderValue_(value) {
        const slider = this.slider_.slider;
        const val = parseFloat(value.value);

        if (slider.options_.handles == 2) {
            const sliderValue = slider.getValue();
            if (value.type == "begin") {
                sliderValue.left = val;
            } else {
                sliderValue.right = val;
            }
            slider.setValue(sliderValue);
        } else {
            slider.setValue(val);
        }

        slider.stop_();
    }

    /**
     * Get the filter object to pass it on to the Layers.
     * 
     * @override
     * @returns {object} filter object
     */
    getActiveFilter() {
        let values = null;
        switch (this.operator_) {
            case ">":
                values = [["gt", this.currentValue_]];
                break;
            case "≥":
                values = [["gteq", this.currentValue_]];
                break;
            case "<":
                values = [["lt", this.currentValue_]];
                break;
            case "≤":
                values = [["lteq", this.currentValue_]];
                break;
            case "=":
                values = [["eq", this.currentValue_]];
                break;
            case "-":
                values = [["bt", this.currentValue_.left, this.currentValue_.right]];
                break;
        }

        if (!values) return {};
        if ((values.length > 0) && this.options_.showNullValues) values.push(["null"])

        return {
            type: "attribute",
            column: this.options_.column,
            values: values,
            excludedLayers: this.getExcludedLayers()
        };
    }

    /**
     * Get the filter's options-object
     */
    getOptions() {
        const activeFilterValues = this.getActiveFilter().values[0];
        const operator = this.operator_;
        const options = super.getOptions();

        options.initialOperator = operator;

        // assign active values
        if (operator == "-") {
            options.initialBegin = activeFilterValues[1];
            options.initialEnd = activeFilterValues[2];
        } else {
            options.initialBegin = activeFilterValues[1];
            options.initialEnd = null;
        }

        return options;
    }

    /**
     * Update the filter based on an existing filter object. Also updates the filter's Ui.
     * 
     * @override
     * @param {object[] | object} filters
     */
    setActiveFilter(filters) {
        if (!Array.isArray(filters)) filters = [filters];

        for (let i = 0; i < filters.length; ++i) {
            const column = filters[i].column;
            const type = filters[i].type.toLowerCase();
            const values = filters[i].values;
            if ((type == "attribute") && (column == this.options_.column) && values.length) {

                const operator = {
                    "eq": "=",
                    "lt": "<",
                    "lteq": "≤",
                    "gt": ">",
                    "gteq": "≥",
                    "bt": "-"
                }[values[0]];

                if (!operator) continue;

                switch (operator) {
                    case ">":
                    case "≥":
                    case "<":
                    case "≤":
                    case "=":
                        if (values.length != 2) continue;
                        this.currentValue_ = values[1]
                        break;
                    case "-":
                        if (values.length != 3) continue;
                        this.currentValue_ = {
                            left: values[1],
                            right: values[2]
                        }
                        break;
                }
                this.operator_ = operator;
                this.initSlider_();
                this.fire("change", this);
                return;
            }
        }
    }
}

class RangeFilterSettings extends FilterSettings {

    htmlExtension = `
        <div><label><span class="input-label" style="width: 80px;">Column: </span><input style="width: calc(100% - 80px);" type="text" name="column"/></label></div>
        <div><label><span class="input-label" style="width: 80px;">Decimals: </span><input style="width: calc(100% - 80px);" type="number" name="decimals" min="0" max="6" step="1"/></label></div>
        <div><label><span class="input-label" style="width: 80px;">Minimum: </span><input style="width: calc(100% - 80px);" type="number" name="minimum"/></label></div>
        <div><label><span class="input-label" style="width: 80px;">Maximum: </span><input style="width: calc(100% - 80px);" type="number" name="maximum"/></label></div>
        <div>
            <label class="input-label" style="width: 80px;">Operators:</label>
            <label title="equals" style="margin-right:20px;"><input type="checkbox" name="operator-equals"/> =</label>
            <label title="range" style="margin-right:20px;"><input type="checkbox" name="operator-range"/> -</label>
            <label title="greater than" style="margin-right:20px;"><input type="checkbox" name="operator-gt"/> &gt;</label>
            <label title="greater thanor equal to" style="margin-right:20px;"><input type="checkbox" name="operator-gteq"/> ≥</label>
            <label title="less than"><input type="checkbox" name="operator-lt"/> &lt;</label>
            <label title="less than or equal to"><input type="checkbox" name="operator-lteq"/> ≤</label>
        </div>
        <div><label><input type="checkbox" name="allow-show-null-values"/> Show "include NULL values" Button</label></div>
    `;

    /**
     * @param {FilterUi} filter
     */
    constructor(filter) {
        super(filter);

        this.query("form").insertAdjacentHTML("beforeend", this.htmlExtension);
    }

    /**
     * Get the currently configured filter options
     */
    getFilterOptions() {
        const options = super.getFilterOptions();

        const decimals = this.query("form input[name='decimals']");
        const minimum = this.query("form input[name='minimum']");
        const maximum = this.query("form input[name='maximum']");

        if (!decimals.checkValidity() || !minimum.checkValidity() || !maximum.checkValidity()) return null;

        options.decimals = parseInt(decimals.value);
        options.begin = parseFloat(minimum.value);
        options.end = parseFloat(maximum.value);
        options.allowShowNullValues = this.query("form input[name='allow-show-null-values']").checked;
        options.column = this.query("form input[name='column']").value;

        const operators = [];
        if (this.query("form input[name='operator-equals']").checked) operators.push("=");
        if (this.query("form input[name='operator-range']").checked) operators.push("-");
        if (this.query("form input[name='operator-gt']").checked) operators.push(">");
        if (this.query("form input[name='operator-gteq']").checked) operators.push("≥");
        if (this.query("form input[name='operator-lt']").checked) operators.push("<");
        if (this.query("form input[name='operator-lteq']").checked) operators.push("≤");
        options.operators = operators;

        return options;
    }

    /**
     * Update Ui based on the filters current options
     */
    updateFilterOptions() {
        super.updateFilterOptions();
        const options = this.filter.options_;

        this.query("form input[name='column']").value = options.column;
        this.query("form input[name='decimals']").value = options.decimals;
        this.query("form input[name='minimum']").value = options.begin;
        this.query("form input[name='maximum']").value = options.end;
        this.query("form input[name='allow-show-null-values']").checked = options.allowShowNullValues;

        this.query("form input[name='operator-equals']").checked = options.operators.includes("=");
        this.query("form input[name='operator-range']").checked = options.operators.includes("-");
        this.query("form input[name='operator-gt']").checked = options.operators.includes(">");
        this.query("form input[name='operator-gteq']").checked = options.operators.includes("≥");
        this.query("form input[name='operator-lt']").checked = options.operators.includes("<");
        this.query("form input[name='operator-lteq']").checked = options.operators.includes("≤");
    }

}